import r2wc from '@r2wc/react-to-web-component';
import { StatsigProvider } from '@statsig/react-bindings';

import { useEssentialProps } from '../../../apps/explore/components/AppWrapper/_app.hooks';
import { Footer } from '../../../apps/explore/components/Footer/Footer';
import { MarketProvider } from '../../../apps/explore/providers/MarketProvider';
import { TranslationProvider } from '../../../apps/explore/providers/TranslationProvider';
import { UserProvider } from '../../../apps/explore/providers/UserProvider';
import { getMarket } from '../../../apps/explore/utils/markets';
import { DEFAULT_SEMBO_MARKET } from './constants';
import {
  useContent,
  usePartners,
  usePaymentMethods,
  useStatsigClient,
} from './hooks';
import { WidgetStyles } from './WidgetStyles';

interface Props {
  'enable-logo'?: boolean;
  'enable-content'?: boolean;
  'open-links-in-new-tab'?: boolean;
  'enable-newsletter'?: boolean;
  'enable-payment-methods'?: boolean;
  'enable-partners'?: boolean;
}

const SemboFooter = ({
  'enable-logo': isLogoSectionEnabled = false,
  'enable-content': isContentSectionEnabled = false,
  'open-links-in-new-tab': openLinksInNewTab = false,
  'enable-newsletter': isNewsletterSectionEnabled = false,
  'enable-payment-methods': isPaymentMethodsSectionEnabled = false,
  'enable-partners': isPartnersSectionEnabled = false,
}: Props) => {
  const market = getMarket(window.location.host) ?? DEFAULT_SEMBO_MARKET;

  const { microcopy } = useEssentialProps(
    { market },
    { skipFetchingMacrocopy: true }
  );
  const { client } = useStatsigClient(market);

  const content = useContent(market, isContentSectionEnabled);
  const paymentMethods = usePaymentMethods(
    market,
    isPaymentMethodsSectionEnabled
  );
  const partners = usePartners(market, isPartnersSectionEnabled);

  const openAuthModal = (origin: string) => {
    window.dispatchEvent(
      new CustomEvent('semboShowAuthModal', {
        detail: { origin },
        bubbles: true,
      })
    );
  };

  if (!microcopy) {
    return null;
  }

  return (
    <MarketProvider market={market}>
      <StatsigProvider client={client}>
        <UserProvider>
          <TranslationProvider microcopy={microcopy} lang={market.lang}>
            <WidgetStyles />

            <Footer
              isLogoSectionEnabled={isLogoSectionEnabled}
              footerContent={content}
              openLinksInNewTab={openLinksInNewTab}
              isNewsletterSectionEnabled={isNewsletterSectionEnabled}
              paymentMethods={paymentMethods}
              proudPartnersCreatorsLogos={partners}
              onAuthClick={openAuthModal}
            />
          </TranslationProvider>
        </UserProvider>
      </StatsigProvider>
    </MarketProvider>
  );
};

customElements.define(
  'sembo-footer',
  r2wc(SemboFooter, {
    props: {
      'enable-logo': 'boolean',
      'enable-content': 'boolean',
      'open-links-in-new-tab': 'boolean',
      'enable-newsletter': 'boolean',
      'enable-payment-methods': 'boolean',
      'enable-partners': 'boolean',
    },
  })
);
